import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll, fmtINR } from "@/components/common";
import { Plus, Pencil, Archive } from "lucide-react";

const EMPTY = { name: "", description: "", sell_rate: "", fee_percent: "0", min_amount: "0", max_amount: "0",
  network: "TRC20", processing_time: "", terms: "", risk_disclosure: "", display_order: 0, active: true };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  usePoll(load, 10000, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, editing);
      else await api.post("/admin/products", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const archive = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Archived"); load(); };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Products</h1>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-product-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
          <Plus className="w-4 h-4" /> New product
        </button>
      </div>

      <div className="space-y-3">
        {products.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between" data-testid={`product-row-${p.id}`}>
            <div>
              <div className="font-semibold text-slate-900">{p.name}</div>
              <div className="text-sm text-slate-500">{fmtINR(p.sell_rate)}/USDT · fee {p.fee_percent}% · {p.network}</div>
              <div className="text-xs text-slate-400 mt-0.5">{p.description}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing({ ...EMPTY, ...p })} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4 text-slate-600" /></button>
              <button onClick={() => archive(p.id)} className="p-2 rounded-lg border border-slate-200 hover:bg-rose-50" data-testid={`archive-product-${p.id}`}><Archive className="w-4 h-4 text-rose-600" /></button>
            </div>
          </div>
        ))}
        {products.length === 0 && <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400">No products.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 overflow-y-auto" onClick={() => setEditing(null)}>
          <form onSubmit={save} className="bg-white rounded-2xl p-6 max-w-lg w-full space-y-3 my-8" onClick={(e) => e.stopPropagation()} data-testid="product-form">
            <h2 className="font-heading font-bold text-lg">{editing.id ? "Edit" : "New"} product</h2>
            {[["name", "Name"], ["description", "Description"], ["sell_rate", "Sell rate (INR/USDT)"], ["fee_percent", "Fee %"],
              ["min_amount", "Min USDT"], ["max_amount", "Max USDT"], ["processing_time", "Processing time"]].map(([k, label]) => (
              <label key={k} className="block"><span className="text-sm font-medium text-slate-700">{label}</span>
                <input value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} data-testid={`product-${k}`}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" required={k === "name" || k === "sell_rate"} /></label>
            ))}
            <label className="block"><span className="text-sm font-medium text-slate-700">Network</span>
              <select value={editing.network} onChange={(e) => setEditing({ ...editing, network: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm">
                <option>TRC20</option><option>BEP20</option></select></label>
            <label className="block"><span className="text-sm font-medium text-slate-700">Terms</span>
              <textarea value={editing.terms} onChange={(e) => setEditing({ ...editing, terms: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
            <div className="flex gap-2">
              <button disabled={busy} data-testid="product-save-button" className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-60">Save</button>
              <button type="button" onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-600">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
